// src/pages/profile-settings/AccountEmailChange.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/sot/primitives/TopBar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  getCurrentAuthEmail,
  isValidAccountEmail,
  normalizeAccountEmail,
  readPendingAccountEmailChange,
  requestAccountEmailChange,
} from "@/pages/profile-settings/accountEmailChangeService.js";

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

function humanEmailChangeError(error, t, isFrench) {
  const raw = String(error?.message || error?.code || error || "").toLowerCase();
  if (raw.includes("duplicate") || raw.includes("already")) {
    return t(
      "settings.edit.email.duplicate",
      isFrench
        ? "Cette adresse e-mail est déjà utilisée par un autre compte AfriMed™."
        : "This email is already used by another AfriMed™ account.",
    );
  }
  if (raw.includes("same_email")) {
    return t(
      "settings.edit.email.sameEmail",
      isFrench ? "Saisissez une nouvelle adresse e-mail." : "Enter a new email address.",
    );
  }
  if (raw.includes("not_authenticated") || raw.includes("auth")) {
    return t(
      "settings.edit.email.notAuthenticated",
      isFrench ? "Session expirée. Veuillez vous reconnecter." : "Session expired. Please sign in again.",
    );
  }
  return (
    error?.message ||
    t(
      "settings.edit.email.changeFailed",
      isFrench
        ? "Impossible de démarrer le changement d’e-mail. Veuillez réessayer."
        : "We could not start the email change. Please try again.",
    )
  );
}

export default function AccountEmailChange({ lane, backRoute }) {
  const nav = useNavigate();
  const { languageCode, t } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const [email, setEmail] = React.useState("");
  const [authEmail, setAuthEmail] = React.useState("");
  const [authEmailConfirmedAt, setAuthEmailConfirmedAt] = React.useState(null);
  const [pending, setPending] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [snackbar, setSnackbar] = React.useState(null);

  const trimmed = normalizeAccountEmail(email);
  const current = normalizeAccountEmail(authEmail);
  const changed = !!trimmed && trimmed !== current;
  const valid = isValidAccountEmail(trimmed);
  const alreadyVerifiedThisEmail = !!current && trimmed === current && !!authEmailConfirmedAt;
  const canRequest = changed && valid && !saving && !pending?.new_email;

  function showConfirmationSnackbar() {
    setSnackbar(
      t(
        "settings.edit.email.secureConfirmationSent",
        isFrench ? "Lien de confirmation sécurisé envoyé" : "Secure confirmation link sent",
      ),
    );
  }

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const auth = await getCurrentAuthEmail();
        if (!mounted) return;
        setAuthEmail(auth.email);
        setAuthEmailConfirmedAt(auth.emailConfirmedAt);
        setEmail(auth.email);
        const stored = readPendingAccountEmailChange();
        if (stored?.new_email && stored?.lane === lane) setPending(stored);
      } catch (e) {
        if (mounted) setErr(humanEmailChangeError(e, t, isFrench));
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isFrench, lane, t]);

  async function handleRequest() {
    setErr("");
    if (!trimmed) {
      setErr(t("settings.edit.email.enterEmail", isFrench ? "Saisissez une adresse e-mail." : "Enter an email address."));
      return;
    }
    if (!valid) {
      setErr(t("settings.edit.email.invalidEmail", isFrench ? "Saisissez une adresse e-mail valide." : "Enter a valid email address."));
      return;
    }
    if (!changed) {
      setErr(
        t(
          "settings.edit.email.sameEmail",
          isFrench ? "Saisissez une nouvelle adresse e-mail." : "Enter a new email address.",
        ),
      );
      return;
    }

    try {
      setSaving(true);
      const result = await requestAccountEmailChange({ lane, newEmail: trimmed });
      setPending({
        request_id: result.request_id,
        lane,
        old_email: result.old_email || current,
        new_email: result.new_email,
        expires_at: result.expires_at,
      });
      showConfirmationSnackbar();
    } catch (e) {
      setErr(humanEmailChangeError(e, t, isFrench));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SettingsScreenShell>
      <TopBar
        title={t("settings.edit.email.title", isFrench ? "Modifier l’e-mail" : "Edit your Email")}
        subtitle={t(
          "settings.edit.email.subtitleSecureLink",
          isFrench
            ? "Nous enverrons un lien de confirmation sécurisé à votre nouvelle adresse e-mail."
            : "We’ll send a secure confirmation link to your new email address.",
        )}
        onBack={() => nav(backRoute)}
        trailing={
          <SaveChip
            label={t(
              "settings.edit.email.sendConfirmation",
              isFrench ? "Envoyer la confirmation" : "Send confirmation",
            )}
            disabled={!canRequest}
            onClick={handleRequest}
          />
        }
      />

      <div className="mt-5 md:mt-6 space-y-3 relative z-[60] pointer-events-auto">
        <TextField56
          label={t("settings.edit.email.label", "Email")}
          value={email}
          onChange={(v) => setEmail(typeof v === "string" ? v : v?.target?.value ?? "")}
          placeholder={t("settings.edit.email.placeholder", "you@example.com")}
          tone="surface"
        />

        {alreadyVerifiedThisEmail && !pending ? (
          <div className="text-body-medium text-on-surface-var">
            {t("settings.edit.email.verified", isFrench ? "Cette adresse e-mail est déjà vérifiée." : "This email is already verified.")}
          </div>
        ) : null}

        {pending?.new_email ? (
          <div className="px-4 text-body-medium text-on-surface-var">
            {t(
              "settings.edit.email.pendingChange",
              isFrench
                ? "Lien de confirmation envoyé. Votre adresse actuelle reste active jusqu’à confirmation."
                : "Confirmation link sent. Your current email remains active until confirmed.",
            )}
          </div>
        ) : null}

        {!!err && <p className="mt-2 text-sm text-[rgb(var(--afrimed-danger))]">{err}</p>}
      </div>

      <Snackbar
        open={!!snackbar}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        duration={6000}
      />
    </SettingsScreenShell>
  );
}
